import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspaceTabs } from '../context/WorkspaceTabsContext';
import financeReconciliationService from '../services/financeReconciliationService';
import { useAppDialog } from '../components/AppDialog';
import FloatingBannerStack from '../components/FloatingBannerStack';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import { AlertIcon, EyeIcon, SearchIcon, TrashIcon } from '../components/Icons';
import {
  FINANCE_RECONCILIATION_UPDATED_EVENT,
  FINANCE_RECONCILIATION_UPDATED_KEY,
  notifyFinanceReconciliationUpdated
} from '../utils/financeReconciliationEvents';
import './FinanceReconciliation.css';

const padDatePart = (value) => String(value).padStart(2, '0');

const getTodayDate = () => {
  const date = new Date();
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
};

const getCurrentMonth = () => getTodayDate().slice(0, 7);

const createEmptyForm = () => ({
  merchant_id: '',
  report_month: getCurrentMonth(),
  title: '',
  notes: '',
  include_ownline: true,
  include_free_wallet: false
});

const labelStyle = {
  display: 'block',
  marginBottom: '0.4rem',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'var(--text-secondary)'
};

const tableHeaderStyle = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontSize: '0.8rem',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap'
};

const tableCellStyle = {
  padding: '0.8rem 1rem',
  fontSize: '0.8125rem',
  verticalAlign: 'top',
  borderBottom: '1px solid var(--border-light)'
};

const amountFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatAmount = (value) => amountFormatter.format(Number(value || 0));

const formatAdminName = (value) => value?.username || value?.email || '-';

const getVarianceStyle = (value) => {
  const amount = Number(value || 0);
  if (amount > 0) return { color: 'var(--success)' };
  if (amount < 0) return { color: 'var(--error)' };
  return { color: 'var(--text-secondary)' };
};

const STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  deleted: 'Deleted'
};

const getStatusStyle = (status) => {
  if (status === 'deleted') return { color: 'var(--error)', backgroundColor: 'rgba(239, 68, 68, 0.1)' };
  if (status === 'approved') return { color: 'var(--success)', backgroundColor: 'rgba(16, 185, 129, 0.1)' };
  if (status === 'submitted') return { color: 'var(--primary)', backgroundColor: 'rgba(var(--primary-rgb), 0.1)' };
  if (status === 'rejected') return { color: 'var(--error)', backgroundColor: 'rgba(239, 68, 68, 0.1)' };
  return { color: 'var(--text-secondary)', backgroundColor: 'var(--bg-soft)' };
};

const FinanceReconciliation = () => {
  const navigate = useNavigate();
  const appDialog = useAppDialog();
  const { activeTabId } = useWorkspaceTabs();
  const wasActiveTab = useRef(activeTabId === '/finance-reconciliation');
  const { admin, hasPermission, getDefaultRoute } = useAuth();
  const canView = hasPermission('finance_reconciliation', 'view');
  const canCreate = hasPermission('finance_reconciliation', 'create');
  const canDeleteReport = admin?.role === 'super_admin';
  const canViewFinancial = admin?.role === 'finance' ||
    hasPermission('finance_reconciliation', 'view_financial') ||
    hasPermission('finance_reconciliation', 'edit_financial');
  const tableColumnCount = canViewFinancial ? 12 : 10;
  const tableMinWidth = canViewFinancial ? '1500px' : '1240px';

  const [reports, setReports] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingReportId, setDeletingReportId] = useState('');
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [reportMonth, setReportMonth] = useState('');
  const [merchantFilter, setMerchantFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(createEmptyForm);
  const latestReportsRequest = useRef(0);

  const fetchReports = useCallback(async ({
    page = 1,
    search = searchTerm,
    month = reportMonth,
    merchantId = merchantFilter,
    status = statusFilter,
    limit = pagination.limit
  } = {}) => {
    const requestId = ++latestReportsRequest.current;
    try {
      setLoading(true);
      setError(null);
      const params = { page, limit, search, report_month: month, merchant_id: merchantId, status };
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const res = await financeReconciliationService.getReports(params);
      if (requestId !== latestReportsRequest.current) return;
      if (res.success) {
        setReports(res.data || []);
        setPagination(prev => ({
          ...prev,
          page: res.pagination.page,
          total: res.pagination.total,
          pages: res.pagination.pages
        }));
      } else {
        setError(res.message || 'Failed to fetch finance reconciliation reports');
      }
    } finally {
      if (requestId === latestReportsRequest.current) setLoading(false);
    }
  }, [merchantFilter, pagination.limit, reportMonth, searchTerm, statusFilter]);

  const fetchMerchants = useCallback(async () => {
    const res = await financeReconciliationService.getMerchants();
    if (res.success) setMerchants(res.data || []);
  }, []);

  useEffect(() => {
    if (!canView) {
      navigate(getDefaultRoute(), { replace: true });
      return;
    }
    fetchMerchants();
  }, [canView, fetchMerchants, getDefaultRoute, navigate]);

  useEffect(() => {
    if (!canView) return undefined;
    const timer = setTimeout(() => fetchReports({ page: 1 }), 400);
    return () => clearTimeout(timer);
  }, [canView, fetchReports, searchTerm, reportMonth, merchantFilter, statusFilter]);

  useEffect(() => {
    if (!canView) return undefined;

    const refreshReports = () => fetchReports({ page: pagination.page });
    const handleStorage = (event) => {
      if (event.key === FINANCE_RECONCILIATION_UPDATED_KEY) refreshReports();
    };

    window.addEventListener(FINANCE_RECONCILIATION_UPDATED_EVENT, refreshReports);
    window.addEventListener('focus', refreshReports);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(FINANCE_RECONCILIATION_UPDATED_EVENT, refreshReports);
      window.removeEventListener('focus', refreshReports);
      window.removeEventListener('storage', handleStorage);
    };
  }, [canView, fetchReports, pagination.page]);

  useEffect(() => {
    const isActiveTab = activeTabId === '/finance-reconciliation';
    if (canView && isActiveTab && !wasActiveTab.current) {
      fetchReports({ page: pagination.page });
    }
    wasActiveTab.current = isActiveTab;
  }, [activeTabId, canView, fetchReports, pagination.page]);

  const statusFilterOptions = useMemo(() => (
    Object.entries(STATUS_LABELS).filter(([value]) => value !== 'deleted' || canDeleteReport)
  ), [canDeleteReport]);

  const summary = useMemo(() => reports.reduce((totals, report) => ({
    reports: totals.reports + 1,
    rows: totals.rows + Number(report.row_count || 0),
    bo: totals.bo + Number(report.bo_winlose_total || 0),
    provider: totals.provider + Number(report.provider_winlose_total || 0),
    variance: totals.variance + Number(report.variance_total || 0),
    company: totals.company + Number(report.company_winlose_total || 0),
    companyAfterMargin: totals.companyAfterMargin + Number(report.company_winlose_after_margin_total || 0)
  }), { reports: 0, rows: 0, bo: 0, provider: 0, variance: 0, company: 0, companyAfterMargin: 0 }), [reports]);

  const openCreateModal = () => {
    setFormData({
      ...createEmptyForm(),
      merchant_id: merchantFilter || ''
    });
    setFormError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setFormError(null);
    setFormData(createEmptyForm());
  };

  const handleFormChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCreateReport = async (event) => {
    event.preventDefault();
    if (!formData.merchant_id) {
      setFormError('Merchant is required');
      return;
    }
    if (!formData.report_month) {
      setFormError('Report month is required');
      return;
    }

    setSaving(true);
    setFormError(null);
    const res = await financeReconciliationService.createReport(formData);
    setSaving(false);

    if (res.success) {
      closeModal();
      setSuccessMessage('Report created');
      notifyFinanceReconciliationUpdated();
      navigate(`/finance-reconciliation/${res.data._id}`);
    } else {
      setFormError(res.message || 'Failed to create report');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setReportMonth('');
    setMerchantFilter('');
    setStatusFilter('');
  };

  const handleDeleteReport = async (report) => {
    const confirmed = await appDialog.confirm({
      title: 'Delete finance report',
      message: 'Delete this finance reconciliation report?',
      confirmLabel: 'Delete',
      variant: 'danger',
      details: [
        { label: 'Report', value: report.title || report._id },
        { label: 'Merchant', value: report.merchant_id?.name || report.merchant_name || '-' },
        { label: 'Month', value: report.report_month || '-' }
      ],
      note: 'This is a soft delete. The report will be hidden from active lists and non-super-admin users.'
    });
    if (!confirmed) return;

    setDeletingReportId(report._id);
    setError(null);
    const res = await financeReconciliationService.deleteReport(report._id);
    setDeletingReportId('');

    if (res.success) {
      setSuccessMessage('Report deleted');
      notifyFinanceReconciliationUpdated();
      const nextPage = reports.length === 1 && pagination.page > 1
        ? pagination.page - 1
        : pagination.page;
      fetchReports({ page: nextPage });
      setTimeout(() => setSuccessMessage(''), 2500);
    } else {
      setError(res.message || 'Failed to delete finance report');
    }
  };

  return (
    <div className="finance-reconciliation-page" style={{ padding: '1rem' }}>
      <section className="card finance-reports-overview" aria-label="Report overview">
        <div className="finance-reports-overview__header">
          <div>
            <div className="finance-reports-overview__title">Report Overview</div>
            <div className="finance-reports-overview__caption">Totals for reports on this page</div>
          </div>
          {canCreate && <button className="btn btn-primary" onClick={openCreateModal}>Create Report</button>}
        </div>
        <div className={`finance-reports-overview__stats${canViewFinancial ? '' : ' finance-reports-overview__stats--compact'}`}>
          <div className="finance-reports-overview__stat">
            <span>Reports</span>
            <strong>{summary.reports}</strong>
          </div>
          <div className="finance-reports-overview__stat">
            <span>Provider Rows</span>
            <strong>{summary.rows}</strong>
          </div>
          <div className="finance-reports-overview__stat">
            <span>BO Win/Lose</span>
            <strong>{formatAmount(summary.bo)}</strong>
          </div>
          <div className="finance-reports-overview__stat">
            <span>Provider Win/Lose</span>
            <strong>{formatAmount(summary.provider)}</strong>
          </div>
          {canViewFinancial && (
            <div className="finance-reports-overview__stat">
              <span>Company Win/Lose</span>
              <strong style={getVarianceStyle(summary.company)}>{formatAmount(summary.company)}</strong>
            </div>
          )}
          {canViewFinancial && (
            <div className="finance-reports-overview__stat">
              <span>Company W/L After Margin</span>
              <strong style={getVarianceStyle(summary.companyAfterMargin)}>{formatAmount(summary.companyAfterMargin)}</strong>
            </div>
          )}
        </div>
      </section>

      <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', alignItems: 'end' }}>
          <div>
            <label style={labelStyle}>Search</label>
            <div className="search-container">
              <div className="search-icon-wrapper"><SearchIcon /></div>
              <input
                type="text"
                className="search-input"
                placeholder="Merchant, title, notes"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Month</label>
            <input
              type="month"
              className="input"
              value={reportMonth}
              onChange={(event) => setReportMonth(event.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>Merchant</label>
            <select className="input" value={merchantFilter} onChange={(event) => setMerchantFilter(event.target.value)}>
              <option value="">All Merchants</option>
              {merchants.map(merchant => (
                <option key={merchant._id} value={merchant._id}>
                  {merchant.name} ({merchant.merchant_code})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select className="input" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">All Status</option>
              {statusFilterOptions.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
        </div>
      </div>

      <FloatingBannerStack
        error={error}
        success={successMessage}
        onDismissError={() => setError('')}
        onDismissSuccess={() => setSuccessMessage('')}
      />

      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="table-striped" style={{ width: '100%', minWidth: tableMinWidth, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '1px solid var(--border-light)' }}>
                <th style={tableHeaderStyle}>Report</th>
                <th style={tableHeaderStyle}>Merchant</th>
                <th style={tableHeaderStyle}>Creator</th>
                <th style={tableHeaderStyle}>Month</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Rows</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>BO Win/Lose</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Provider Win/Lose</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Win / Lose</th>
                {canViewFinancial && (
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Company Win/Lose</th>
                )}
                {canViewFinancial && (
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Company W/L After Margin</th>
                )}
                <th style={tableHeaderStyle}>Status</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && reports.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnCount} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading reports...</td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnCount} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No reports found.</td>
                </tr>
              ) : reports.map(report => {
                const status = report.is_deleted ? 'deleted' : (report.status || 'draft');

                return (
                  <tr key={report._id}>
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: 700 }}>{report.title || report._id}</div>
                      {report.notes && <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{report.notes}</div>}
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: 700 }}>{report.merchant_id?.name || report.merchant_name || '-'}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{report.merchant_id?.merchant_code || report.merchant_code || '-'}</div>
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: 700 }}>{formatAdminName(report.created_by)}</div>
                    </td>
                    <td style={tableCellStyle}>{report.report_month}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 700 }}>{report.row_count || 0}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 700 }}>{formatAmount(report.bo_winlose_total)}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 700 }}>{formatAmount(report.provider_winlose_total)}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 800, ...getVarianceStyle(report.variance_total) }}>{formatAmount(report.variance_total)}</td>
                    {canViewFinancial && (
                      <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 800, ...getVarianceStyle(report.company_winlose_total) }}>{formatAmount(report.company_winlose_total)}</td>
                    )}
                    {canViewFinancial && (
                      <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 800, ...getVarianceStyle(report.company_winlose_after_margin_total) }}>{formatAmount(report.company_winlose_after_margin_total)}</td>
                    )}
                    <td style={tableCellStyle}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        minHeight: '26px',
                        padding: '0.25rem 0.55rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 800,
                        ...getStatusStyle(status)
                      }}>
                        {STATUS_LABELS[status] || status}
                      </span>
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.4rem', flexWrap: 'wrap' }}>
                        <button className="btn" onClick={() => navigate(`/finance-reconciliation/${report._id}`)} style={{ padding: '0.45rem 0.7rem', color: 'var(--primary)', gap: '0.35rem' }}>
                          <EyeIcon size={14} />
                          Detail
                        </button>
                        {canDeleteReport && !report.is_deleted && (
                          <button
                            className="btn"
                            onClick={() => handleDeleteReport(report)}
                            disabled={deletingReportId === report._id}
                            style={{ padding: '0.45rem 0.7rem', color: 'var(--error)', gap: '0.35rem' }}
                          >
                            <TrashIcon size={14} />
                            {deletingReportId === report._id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={pagination}
          onPageChange={(page) => fetchReports({ page })}
          onLimitChange={(limit) => {
            setPagination(prev => ({ ...prev, limit }));
            fetchReports({ page: 1, limit });
          }}
          loading={loading}
          itemName="reports"
        />
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title="Create Finance Report" maxWidth="720px">
        <form onSubmit={handleCreateReport}>
          {formError && (
            <div style={{ padding: '0.85rem', marginBottom: '1rem', border: '1px solid var(--error)', borderRadius: 'var(--radius-md)', color: 'var(--error)', backgroundColor: 'rgba(239, 68, 68, 0.08)', display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
              <AlertIcon size={16} />
              <span>{formError}</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Merchant *</label>
              <select name="merchant_id" className="input" value={formData.merchant_id} onChange={handleFormChange} required>
                <option value="">Select Merchant</option>
                {merchants.map(merchant => (
                  <option key={merchant._id} value={merchant._id}>
                    {merchant.name} ({merchant.merchant_code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Month *</label>
              <input type="month" name="report_month" className="input" value={formData.report_month} onChange={handleFormChange} required />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Title</label>
              <input type="text" name="title" className="input" value={formData.title} onChange={handleFormChange} placeholder="Optional" />
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Notes</label>
            <textarea name="notes" className="input" value={formData.notes} onChange={handleFormChange} rows={3} style={{ resize: 'vertical' }} />
          </div>
          <fieldset style={{ border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', padding: '0.85rem 1rem', marginBottom: '1rem' }}>
            <legend style={{ padding: '0 0.35rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Report Settings</legend>
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.8125rem', fontWeight: 600 }}>
                <input type="checkbox" name="include_ownline" checked={formData.include_ownline} onChange={handleFormChange} />
                Include Ownline
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.8125rem', fontWeight: 600 }}>
                <input type="checkbox" name="include_free_wallet" checked={formData.include_free_wallet} onChange={handleFormChange} />
                Include Free Wallet
              </label>
            </div>
          </fieldset>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Create Report'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default FinanceReconciliation;
